import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Heart, Award, Users, Music } from 'lucide-react';

export const AboutSection = () => {
  const achievements = [
    {
      icon: <Award className="w-6 h-6" />,
      number: "15+",
      label: "Years of Ministry"
    },
    {
      icon: <Users className="w-6 h-6" />,
      number: "500+",
      label: "Events Performed"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      number: "1000+",
      label: "Hearts Touched"
    },
    {
      icon: <Music className="w-6 h-6" />,
      number: "50+",
      label: "Original Arrangements"
    }
  ];

  return (
    <section id="about" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
              Meet Nadja Scott
            </h2>
            <p className="text-xl text-red-600 font-semibold mb-6">
              Founder and Girl Choirmaster
            </p>
            
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Nadja Scott is a best-selling author, acclaimed playwright, award-winning vocalist, producer, and creative visionary whose voice and pen continue to shift atmospheres across gospel, theatrical, and literary stages. The oldest of three children, Nadja discovered her love for music at the age of six, singing alongside her mother, Tamara Scott, who also shared a deep passion for piano and worship. Quickly recognized for her extraordinary gift, she was accepted into the prestigious Peabody Conservatory of Music in Baltimore, Maryland, where she trained extensively under renowned instructors including Dr. Doreen Falby and Dr. Marc Irwin, specializing in classical soprano, choral arts, and orchestration.
            </p>
            
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              As a celebrated coloratura soprano, Nadja performed with the Peabody Children's Chorus until age seventeen, earning multiple accolades, including the Arts for Talented Youth Award for excellence, discipline, and performance mastery. She has performed iconic works such as Bach's Magnificat, portrayed Susanna in Le nozze di Figaro, Frasquita in Carmen, and graced stages in professional opera and theater productions including Disney's "The Lion King" and starring as "Belle" in Beauty and the Beast.
            </p>
            
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Though her classical career flourished, Nadja's roots were grounded in the church, deeply nurtured by her mother and grandmother, who instilled in her a strong biblical foundation and a commitment to prayer, humility, and service. Her passion for Gospel music led her back to ministry, where she became a leading voice at Miracle Temple Worship Center and a sought-after worship leader throughout the DMV.
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              She has shared the stage with some of gospel and inspirational music's most influential voices, including Shirley Caesar, Marvin Sapp, Juanita Bynum, Yolanda Adams, Donnie McClurkin, Crystal Aikin, Jason and Jonathan Nelson, Patti LaBelle, and many more.
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Nadja is the founder and visionary of the Divine Anointing Choir and previously directed the Johns Hopkins University Gospel Choir from 2005–2008. She is celebrated not only for her powerhouse vocals but also for her vocal coaching expertise, specializing in breathing, phrasing, diction, ear training, and choral excellence.
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Nadja Scott has emerged as a powerful voice in literature and theater, becoming a best-selling author and nationally recognized playwright. Through her company, Top Notch Productions, she writes and produces spiritually impactful, culturally relevant stage plays that blend faith, humor, raw emotion, and social commentary—captivating audiences and transforming lives.
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Her works include dynamic gospel stage plays such as: <span className="font-semibold text-white">Covered… But Not by Y'all</span>, <span className="font-semibold text-white">Operation: Block the Blessing</span>, <span className="font-semibold text-white">Not Today, Jesus!</span>, and <span className="font-semibold text-white">When She Speaks: The Depth of a Black Woman</span>. Her books and theatrical stories empower audiences to heal, laugh, cry, confront truth, and return to God's heart.
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed italic">
              A servant at heart, Nadja believes, "We are not called to be light only in the church—we are called to shine in every room God places us in." Whether on stage, in print, or behind a microphone, her purpose is to reveal Christ through authentic storytelling, worship, and creative excellence.
            </p>

            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Now, as a dynamic creative force—singer, minister, vocal instructor, choir director, best-selling author, playwright, producer, and founder of multiple arts and faith initiatives—Nadja continues to break barriers and inspire the next generation of worshippers, thinkers, and creators. Her voice is not just heard—it is felt. Her words are not just read—they are lived. Her productions are not just seen—they are experienced.
            </p>

            {/* Achievements Grid */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 text-red-600">
                    {achievement.icon}
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{achievement.number}</div>
                  <div className="text-sm text-gray-300">{achievement.label}</div>
                </div>
              ))}
            </div>

            <a 
              href="https://docs.google.com/forms/d/12hphFMKBt-Zd44mV26mRSBOZkxx0veNAx_E3yN2YRw8/viewform"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full text-lg">
                Connect with Us!
              </Button>
            </a>
          </div>

          {/* Photos Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/qq8te3p2_IMG_8832.jpeg"
                alt="Nadja Scott Professional Photo"
                className="w-full h-64 object-cover object-top rounded-2xl shadow-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/z0nsybaz_IMG_8830.jpeg"
                alt="Nadja Scott Portrait"
                className="w-full h-48 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
            <div className="mt-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/c33bjf2c_IMG_8840.jpeg"
                alt="Nadja Scott Artist Photo"
                className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};